import { PageTitle } from '_metronic/layout/core'

import Select from 'react-select'
import TabsButtonGroup from 'Presentation/Components/Tabs/ButtonGroup'
import BillPlanButton from './Components/BillPlanButton'
import PricingPlan from 'Presentation/Components/PricingPlan'
import { ClientSideTable } from 'Presentation/Components/Table'
import ReactSwitch from 'react-switch'
import PaymentMethod from 'Presentation/Views/PaymentMethod'

import useViewModel from './ViewModel'
import { TABS } from '../Config'
import { SUPPLIER_PLAN_FEATURES } from 'Data/plan-feature'
import formatNumberCommas from 'Utils/formatNumberCommas'
import clsx from 'clsx'

//////////
// View //
//////////

const ProfilePlanView: React.FC = () => {
	const {
		currentPlan,
		companyName,
		currentTab,
		onChangeTab,
		data,
		TABLE_CONFIGS,
		isYear,
		plans,
		promptPayData,
		isOpenPaymentMethod,
		isSubmitting,
		planPrice,
		setIsYear,
		onPurchase,
		onPurchaseConfirmed,
		onPurchaseCancel,
	} = useViewModel()

	return (
		<ViewPageTitle companyName={companyName}>
			<PaymentMethod
				hideBankTransfer
				isOpen={isOpenPaymentMethod}
				totalAmount={planPrice}
				onSubmit={onPurchaseConfirmed}
				onClose={onPurchaseCancel}
				disabled={isSubmitting}
				promptPayData={promptPayData}
			/>

			<div className="row gy-5">
				<div className="col-12 d-none d-xl-block">
					<TabsButtonGroup
						manualActiveTab={currentTab}
						changeTab={onChangeTab}
						tabNames={TABS.map(tab => tab.tabName)}
						tabContents={[<></>]}
					/>
				</div>

				<div className="col-12 d-xl-none">
					<Select
						isSearchable={false}
						options={TABS.map((tab, idx) => ({ value: idx, label: tab.tabName }))}
						value={{ label: TABS.find((_, idx) => idx === currentTab)?.tabName }}
						styles={{
							control: (baseStyles, state) => ({
								...baseStyles,
								border: '1px solid #e1e3ea',
								height: 40,
							}),
							menu: (baseStyles, state) => ({
								...baseStyles,
								zIndex: 9999,
							}),
						}}
						onChange={(selected: any) => {
							if (selected !== null) onChangeTab(Number(selected?.value))
						}}
					/>
				</div>

				<div className="col-12">
					<div className="card border border-kumopack-grey-200">
						<div className="card-body">
							<div className="row gx-10 gy-3">
								<div className="col-6">
									<div className="mb-2">
										<span className="me-4 fw-bold text-kumopack-grey-900">Current Plan</span>
										<span
											className="d-inline-flex flex-row align-items-center"
											style={{ gap: '8px' }}>
											<span
												className={clsx({
													'text-kumopack-primary-600': !isYear,
												})}>
												Monthly
											</span>
											<ReactSwitch
												checked={isYear}
												onChange={checked => setIsYear(checked)}
												onColor="#B15FCE"
												offColor="#B15FCE"
												offHandleColor="#ffffff"
												onHandleColor="#ffffff"
												uncheckedIcon={false}
												checkedIcon={false}
												height={20}
												width={36}
												boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
												activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
												className="react-switch"
											/>
											<span
												className={clsx({
													'text-kumopack-primary-600': isYear,
												})}>
												Yearly
											</span>
										</span>
									</div>
									<div className="text-kumopack-grey-600">
										We'll credit your account if you need to downgrade during the billing cycle.
									</div>
								</div>
								<div className="col-6">
									<div className="row gy-5">
										<div className="col-12">
											<BillPlanButton
												title="Standard plan"
												value="standard"
												price="Free"
												description="Includes up to 10 users, 20GB individual data and access to all features."
												active={(currentPlan?.slug ?? '').toLowerCase() === 'standard'}
												disabled={true}
											/>
										</div>

										{plans.map((plan, idx) => (
											<div className="col-12" key={`bill-plan-button-${idx}`}>
												<BillPlanButton
													active={plan.name
														.toLowerCase()
														.includes((currentPlan?.slug ?? '').toLowerCase())}
													title={plan.name}
													price={`${formatNumberCommas(plan.price ?? 0)} THB/ ${
														isYear ? 'yearly' : 'monthly'
													}`}
													description="Includes up to 10 users, 20GB individual data and access to all features."
													value={plan.uuid}
													disabled={
														isSubmitting ||
														plan.name
															.toLowerCase()
															.includes((currentPlan?.slug ?? '').toLowerCase())
													}
													onChange={() => {
														if (
															!isSubmitting &&
															!plan.name
																.toLowerCase()
																.includes((currentPlan?.slug ?? '').toLowerCase())
														) {
															onPurchase(plan.uuid)
														}
													}}
												/>
											</div>
										))}
									</div>
								</div>

								<div className="col-12">
									<PricingPlan data={SUPPLIER_PLAN_FEATURES} />
								</div>

								<div className="col-12">
									<div className="card-body px-4 py-4">
										<div className="card border-1px border-kumopack-grey-200">
											<div className="card-body pt-2 px-0">
												<ClientSideTable
													isGrey
													items_per_page={10}
													data={data}
													columns={TABLE_CONFIGS}
												/>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</ViewPageTitle>
	)
}

/////////////////////
// View Page Title //
/////////////////////

const ViewPageTitle: React.FC<{ children: React.ReactNode; companyName: string }> = ({
	children,
	companyName,
}) => {
	return (
		<>
			<PageTitle
				breadcrumbs={[
					{
						title: 'My Account',
						path: '/profile',
						isActive: false,
						isSeparator: false,
					},
					{
						title: 'Plan',
						path: '#',
						isActive: true,
						isSeparator: false,
					},
				]}>
				{companyName}
			</PageTitle>

			{children}
		</>
	)
}

export default ProfilePlanView
